import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Card, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { ProjectGridField } from '@/types/fields'
import useProjectFieldInfos from '@/hooks/useProjectFieldInfos'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import {
  useGetProjectGridFieldRules,
  useUpdateProjectGridFieldRule,
} from '@/service-library/hooks/project-grid-field-rules'
import queryKeys from '@/service-library/query-keys'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import FieldRules from '@/components/project-settings/field-rules/FieldRules'
import RuleSettings, { FormValues } from '@/components/rules/RuleSettings'
import FieldRulesFieldList from './FieldRulesFieldList'

export default function FieldRulesPage() {
  const { updateProjectGridFieldRule, isPending: isUpdatingRule } =
    useUpdateProjectGridFieldRule({
      sideEffectQueryKeys: [queryKeys.projectGridFieldRules.all],
      onSuccess: () => {
        showSuccessSnackbar('Field Rule Updated')
      },
      onError: () => {
        showErrorSnackbar('Field Rule Update Failed')
      },
    })

  const { fieldInfos, allFields } = useProjectFieldInfos()

  const [selectedField, setSelectedField] = useState<ProjectGridField | null>(
    null,
  )

  const fieldRulesQuery = useGetProjectGridFieldRules({
    filters: {
      project_grid_field_id: selectedField?.id,
      fields__include: 'rule,workflow_states_ids,dependent_fields_ids',
      limit: '1000',
    },
    enabled: !!selectedField?.id,
  })

  const { projectGridFieldRules, isPending: rulesIsLoading } = fieldRulesQuery

  const defaultRuleId = useMemo(
    () =>
      selectedField
        ? projectGridFieldRules[0]?.id
        : fieldInfos[0].field?.project_grid_field_rules?.[0]?.id,
    [fieldInfos, projectGridFieldRules, selectedField],
  )

  const {
    value: selectedRuleId,
    isInUrl,
    toggleValue,
    resetValue,
  } = useStatefulSearchParam({
    param: 'rule_id',
  })

  const selectedRule = projectGridFieldRules.find(
    (rule) => rule.id === selectedRuleId,
  )

  const setSelectedRuleId = useCallback(
    (ruleId: string | undefined) => {
      if (ruleId) {
        toggleValue(ruleId)
        return
      }

      if (defaultRuleId) {
        toggleValue(defaultRuleId)
      } else if (isInUrl) {
        resetValue()
      }
    },
    [defaultRuleId, isInUrl, resetValue, toggleValue],
  )

  const updateFieldRule = async ({
    workflow_states_ids,
    dependent_fields,
    is_superuser_rule,
    ...rest
  }: FormValues) => {
    if (selectedRule) {
      return updateProjectGridFieldRule({
        ...selectedRule,
        dependent_fields_ids: dependent_fields.map(({ id }) => id),
        workflow_states_ids,
        is_superuser_rule,
        params: rest,
      })
    }
    return
  }

  useEffect(() => {
    if (!selectedField) {
      let fieldToSelect = fieldInfos[0].field
      if (isInUrl) {
        const field = allFields.find((f) =>
          f.project_grid_field_rules?.some((r) => r.id === selectedRuleId),
        )
        if (field) {
          fieldToSelect = field
        }
      }
      setSelectedField(fieldToSelect)
    }
  }, [allFields, fieldInfos, isInUrl, selectedField, selectedRuleId])

  useEffect(() => {
    if (selectedField?.id && !rulesIsLoading && !selectedRule) {
      setSelectedRuleId(undefined)
    }
  }, [rulesIsLoading, selectedField?.id, selectedRule, setSelectedRuleId])

  if (!selectedField) return null

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <ProjectBreadcrumb label="Field Rules" url="../field-rules" />
      <Grid container spacing={2} sx={{ maxWidth: '100%' }}>
        <Grid size={12}>
          <Typography variant="h5" color="textPrimary">
            Field Rules
          </Typography>
        </Grid>

        <Grid size={3} sx={{ height: 'calc(100vh - 165px)' }}>
          <FieldRulesFieldList
            fieldInfos={fieldInfos}
            selectedField={selectedField}
            setSelectedField={setSelectedField}
          />
        </Grid>

        <Grid size={9}>
          <Card sx={{ p: 1, height: '100%' }}>
            <Grid container columnSpacing={2}>
              {/* <Grid size={12}>
                <RuleTesterSection />
                <Divider />
              </Grid> */}
              <Grid size={12}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Rules
                </Typography>
              </Grid>
              <Grid size={6}>
                <FieldRules
                  field={selectedField}
                  fieldRulesQuery={fieldRulesQuery}
                  selectedRuleId={selectedRuleId}
                  setSelectedRuleId={setSelectedRuleId}
                />
              </Grid>
              {selectedRule && (
                <Grid size={6}>
                  <RuleSettings
                    key={selectedRule.id} // Completely rerender each time we change the selected rule
                    fieldId={selectedField.id}
                    isLoading={isUpdatingRule}
                    onSubmit={updateFieldRule}
                    rule={selectedRule}
                    fields={allFields}
                  />
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
